import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
interface VideoSectionProps {
  src: string;
  fullBleed?: boolean;
  priority?: boolean;
}
export function VideoSection({
  src,
  fullBleed = false,
  priority = false
}: VideoSectionProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const reduce = useReducedMotion();

  // Auto-pause when scrolled out of view to save battery/data
  useEffect(() => {
    if (!videoRef.current) return;

    // Attempt to play immediately if it's a priority video
    if (priority) {
      videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          videoRef.current?.play().then(() => setIsPlaying(true)).catch(() => {});
        } else {
          videoRef.current?.pause();
          setIsPlaying(false);
        }
      });
    }, {
      threshold: 0.1
    });
    observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, [priority]);
  return <section className={`w-full bg-white ${fullBleed ? 'pt-16 sm:pt-24' : ''}`} data-id="element-470" data-mp="13tlk6lvovl23">
      <div className={fullBleed ? 'w-full' : 'mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 md:py-16'} data-id="element-471" data-mp="18tq28gc8wnuf">
        <motion.div initial={reduce ? undefined : {
        opacity: 0,
        y: 20
      }} whileInView={reduce ? undefined : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} className={`relative w-full overflow-hidden bg-ink/5 ${fullBleed ? 'aspect-[16/9] sm:aspect-[21/9]' : 'aspect-[2.4/1] sm:aspect-[21/9]'}`} data-id="element-472" data-mp="1dqq6qmtg5ijf">
          <video ref={videoRef} src={src} className="h-full w-full object-cover" playsInline muted loop autoPlay={priority} preload={priority ? 'auto' : 'metadata'} aria-label="Kia SUV promotional video" data-id="element-473" data-mp="21ix0de7sfbf5" />
          
          {/* Play/Pause toggle for accessibility */}
          <button type="button" onClick={() => {
          if (videoRef.current) {
            if (isPlaying) {
              videoRef.current.pause();
            } else {
              videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
          }
        }} className="absolute bottom-4 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-ink/40 text-white backdrop-blur-sm transition-colors hover:bg-ink/60" aria-label={isPlaying ? 'Pause video' : 'Play video'} data-id="element-474" data-mp="1gs1iukr3tf0t">
            {isPlaying ? <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24" data-id="element-475" data-mp="57dwmovegbrj">
                <path d="M6 4h4v16H6zm8 0h4v16h-4z" data-id="element-476" data-mp="2gu7oiauzsvcl" />
              </svg> : <svg className="h-3 w-3 ml-0.5" fill="currentColor" viewBox="0 0 24 24" data-id="element-477" data-mp="2lqy46x2vxoxh">
                <path d="M8 5v14l11-7z" data-id="element-478" data-mp="273v6apanhe9m" />
              </svg>}
          </button>
        </motion.div>
      </div>
    </section>;
}